/**
 * PATH: packages/ui/src/kanban/components/card-detail/CardEmailTab.tsx
 * FILE_ID: UI-KANBAN-CMP-CARD-EMAIL-TAB-001
 * PURPOSE: Render the card-detail drawer's "Email" tab for a card that has one
 *          OR MANY linked emails. A selector strip lists every linked email
 *          (primary first, then related); the full <EmailThreadView> reader
 *          renders the currently-selected one. A single-email card shows just
 *          the reader (no strip) — byte-identical to the pre-existing behaviour.
 * WHY:     Operator 2026-06-29: "the ability to add multiple emails in the
 *          kanban card is not working properly … it should be expandable and no
 *          bugs." A card carries `primary_email` + N `related_emails`, but the
 *          Email tab only ever mounted <EmailThreadView emailId={primary_email}>
 *          and dropped the related ones — so the full reader could open exactly
 *          ONE email no matter how many were attached, and the Card-tab box's
 *          "Open full email →" always landed on the primary (wrong email for a
 *          related row). Every linked email is now selectable and fully readable
 *          here.
 * FLOW:
 *   ┌──────────────┐   ┌───────────────────┐   ┌─────────────────────────────┐
 *   │ emailIds[]   │ → │ selector chips    │ → │ <EmailThreadView>           │
 *   │ (primary 1st)│   │ (selectedId)      │   │ for the selected email      │
 *   └──────────────┘   └───────────────────┘   └─────────────────────────────┘
 * RELATED:
 *   - CardDetailDrawer.tsx: mounts this for the 'email' tab.
 *   - card-detail/linkedEmails.ts: orderLinkedEmailIds (primary-first, deduped).
 *   - card-detail/LinkedEmailRow.tsx: the Card-tab inline counterpart.
 *   - EmailThreadView.tsx: the full reader this drives.
 *   - card-detail/emailRowMapping.ts: splitFromLine (chip sender fallback).
 */
import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'

import { EmailThreadView } from '../EmailThreadView'
import { EmailBoardsPopover } from './EmailBoardsPopover'
import { splitFromLine } from './emailRowMapping'
import type { KanbanApi, KanbanEmailAdapter } from '../../api'

export interface CardEmailTabProps {
  /** Ordered, de-duplicated linked-email ids (primary first). Non-empty —
   *  the drawer renders its own empty state when there are none. */
  emailIds: string[]
  adapter: KanbanEmailAdapter
  /** Subject shown by the reader before its own fetch resolves. */
  fallbackSubject?: string
  /** Email id to open first — set when the operator clicked "Open full
   *  email" on a SPECIFIC linked row (BUG-2026-06-29: that used to always
   *  land on the primary). Ignored if it isn't in `emailIds`. */
  initialSelectedId?: string | null
  /** ARC-1: the card the tab belongs to — threaded into the reader's
   *  inline composer so sent messages link back to the card. */
  cardId?: string | null
  /** ARC-1 MR3: tenant kanban api — powers the "Boards" allocation
   *  popover (multi-board add + reallocate). Optional; when absent (or
   *  when `api.mirrorCardToBoard` isn't wired) the popover hides. */
  api?: KanbanApi
  /** P34 — org clamp for the Boards popover picker. */
  organisationId?: number | null
}

/**
 * One selector chip for a linked email. Reuses the SAME
 * ['kanban','card-linked-email', id] preview query the inline LinkedEmailRow
 * warms, so switching emails here is free once the Card-tab box (or a prior
 * chip) has fetched it. Shows a primary/#n badge + the email subject (falling
 * back to the sender, then a positional label) so the operator can tell the
 * threads apart before opening each.
 */
function EmailTabChip({
  emailId,
  adapter,
  cardId,
  index,
  isPrimary,
  active,
  onSelect,
}: {
  emailId: string
  adapter: KanbanEmailAdapter
  cardId?: string | null
  index: number
  isPrimary: boolean
  active: boolean
  onSelect: () => void
}) {
  const previewQ = useQuery({
    queryKey: ['kanban', 'card-linked-email', emailId, cardId ?? null],
    queryFn: () => adapter.getEmail(emailId, { cardId }),
    enabled: !!emailId,
    staleTime: 30_000,
  })

  const subject = previewQ.data?.subject?.trim()
  const sender = splitFromLine(previewQ.data?.from).name
  const label = subject || sender || `Email ${index + 1}`
  const badge = isPrimary ? 'Primary' : `#${index + 1}`

  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={active}
      title={`${badge}${subject ? ` · ${subject}` : ''}${
        previewQ.data?.from ? ` · ${previewQ.data.from}` : ''
      }`}
      className={[
        'inline-flex max-w-[220px] shrink-0 items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors',
        active
          ? 'border-blue-300 bg-blue-50 text-blue-800 dark:border-blue-500/50 dark:bg-blue-950/40 dark:text-blue-200'
          : 'border-neutral-200 bg-white text-neutral-600 hover:border-neutral-300 hover:bg-neutral-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-300 dark:hover:bg-zinc-800',
      ].join(' ')}
    >
      <span
        className={[
          'rounded-sm px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wide',
          isPrimary
            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200'
            : 'bg-neutral-100 text-neutral-500 dark:bg-zinc-800 dark:text-zinc-400',
        ].join(' ')}
      >
        {badge}
      </span>
      <span className="truncate">{label}</span>
    </button>
  )
}

export function CardEmailTab({
  emailIds,
  adapter,
  fallbackSubject,
  initialSelectedId,
  cardId,
  api,
  organisationId = null,
}: CardEmailTabProps) {
  const queryClient = useQueryClient()
  const [selectedId, setSelectedId] = useState<string | null>(
    initialSelectedId && emailIds.includes(initialSelectedId)
      ? initialSelectedId
      : emailIds[0] ?? null,
  )

  // ARC-1 MR2/MR3: the card's server-side state changed (inline composer
  // send linked a new message, or a board allocation was added/moved).
  // Invalidate the card + linked-email caches so the drawer repaints
  // without a reload.
  const onComposerSent = () => {
    if (cardId) {
      void queryClient.invalidateQueries({ queryKey: ['kanban', 'card', cardId] })
    }
    void queryClient.invalidateQueries({
      queryKey: ['kanban', 'card-linked-email'],
    })
  }

  // Honour a NEW requested email (the operator clicked "Open full email" on a
  // specific linked row after the tab was already mounted).
  useEffect(() => {
    if (initialSelectedId && emailIds.includes(initialSelectedId)) {
      setSelectedId(initialSelectedId)
    }
  }, [initialSelectedId, emailIds])

  // Keep the selection valid if the linked set changes underneath us (an
  // email gets attached/removed while the drawer is open). Falls back to the
  // primary so the reader never points at a now-unlinked id.
  useEffect(() => {
    if (!selectedId || !emailIds.includes(selectedId)) {
      setSelectedId(emailIds[0] ?? null)
    }
  }, [emailIds, selectedId])

  const multi = emailIds.length > 1
  // ARC-1 MR3 — the "Boards" allocation popover needs the kanban api
  // (mirror-to-board), the card, and a selected email to act on.
  const showBoards = Boolean(
    api?.mirrorCardToBoard && cardId && selectedId,
  )

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Toolbar strip — email selector chips (multi-email cards) and the
          Boards allocation popover. A single-email card without the
          popover renders no strip at all (byte-identical to before). */}
      {(multi || showBoards) && (
        <div
          role="group"
          aria-label="Linked emails"
          className="flex shrink-0 items-center gap-1.5 overflow-x-auto border-b border-neutral-200 bg-neutral-50/60 px-3 py-1.5 dark:border-zinc-800 dark:bg-zinc-900/40"
        >
          {multi &&
            emailIds.map((id, idx) => (
              <EmailTabChip
                key={id}
                emailId={id}
                adapter={adapter}
                cardId={cardId}
                index={idx}
                isPrimary={idx === 0}
                active={id === selectedId}
                onSelect={() => setSelectedId(id)}
              />
            ))}
          {showBoards && api && cardId && selectedId && (
            <div className="ml-auto shrink-0">
              <EmailBoardsPopover
                cardId={cardId}
                emailId={selectedId}
                api={api}
                adapter={adapter}
                organisationId={organisationId}
                onChanged={onComposerSent}
              />
            </div>
          )}
        </div>
      )}

      {/* Full reader for the selected email. Keyed on the id so internal state
          (thread switcher, inline-image recovery) resets cleanly per email.

          READER CONVERGENCE ARC — when the tenant injects the Email Hub's
          canonical reader (adapter.FullEmailReader), mount THAT: it carries
          the hub's entire capability set (search, tags, right-click, smart
          actions, assign rail — operator 2026-07-06). The in-package
          EmailThreadView fork remains ONLY as the fallback for read-only
          bundles that don't ship the hub. */}
      <div className="min-h-0 flex-1">
        {adapter.FullEmailReader && selectedId ? (
          <adapter.FullEmailReader
            key={selectedId}
            emailId={selectedId}
            cardId={cardId}
            onChanged={onComposerSent}
          />
        ) : (
          <EmailThreadView
            key={selectedId ?? 'none'}
            emailId={selectedId}
            adapter={adapter}
            fallbackSubject={fallbackSubject}
            cardId={cardId}
            onComposerSent={onComposerSent}
          />
        )}
      </div>
    </div>
  )
}
